import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { FitnessHexagon } from '../../../assets/images';

const RadarChart = ({
    data = {
        endurance: 75, // 심폐지구력
        strength: 60, // 근력근지구력
        balance: 80, // 평형성
        flexibility: 70, // 유연성
        reaction: 85, // 순발력
        cooperation: 65, // 협응력
    },
    size = 100,
    showLabels = true,
    showBackground = true,
    sx = {},
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    // 데이터 정규화 (0-100 범위로)
    const normalizeData = value => {
        // undefined, null, NaN 체크
        const numValue = Number(value);
        if (isNaN(numValue) || value === null || value === undefined) {
            return 0;
        }
        return Math.max(0, Math.min(100, numValue));
    };

    const normalizedData = {
        endurance: normalizeData(data?.endurance),
        strength: normalizeData(data?.strength),
        balance: normalizeData(data?.balance),
        flexibility: normalizeData(data?.flexibility),
        reaction: normalizeData(data?.reaction),
        cooperation: normalizeData(data?.cooperation),
    };

    // 6각형 좌표 계산 (정확한 위치)
    const getPolygonPoints = () => {
        const centerX = 50;
        const centerY = 50;
        const maxRadius = 40; // 최대 반지름

        // 6각형의 각 축 방향 (시계방향으로 12시부터 시작)
        const angles = [
            -Math.PI / 2, // 12시 (심폐지구력)
            -Math.PI / 6, // 2시 (순발력)
            Math.PI / 6, // 4시 (유연성)
            Math.PI / 2, // 6시 (근력근지구력)
            (Math.PI * 5) / 6, // 8시 (협응력)
            (-Math.PI * 5) / 6, // 10시 (평형성)
        ];

        const dataValues = [
            normalizedData.endurance, // 12시
            normalizedData.reaction, // 2시
            normalizedData.flexibility, // 4시
            normalizedData.strength, // 6시
            normalizedData.cooperation, // 8시
            normalizedData.balance, // 10시
        ];

        return dataValues
            .map((value, index) => {
                const radius = (value / 100) * maxRadius;
                const x = centerX + radius * Math.cos(angles[index]);
                const y = centerY + radius * Math.sin(angles[index]);
                return `${x},${y}`;
            })
            .join(' ');
    };

    return (
        <Box
            sx={{
                width: size,
                height: size,
                position: 'relative',
                ...sx,
            }}
        >
            {/* Background Hexagon */}
            {showBackground && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: size,
                        height: size,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <FitnessHexagon color="#00BFFF" size={size} />
                </Box>
            )}

            {/* Data Polygon - SVG로 실제 데이터 반영 */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: size,
                    height: size,
                }}
            >
                <svg
                    width={size}
                    height={size}
                    viewBox="0 0 100 100"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                >
                    <defs>
                        <linearGradient
                            id="fitnessGradient"
                            x1="0%"
                            y1="0%"
                            x2="100%"
                            y2="100%"
                        >
                            <stop
                                offset="0%"
                                stopColor="rgba(34, 189, 255, 0.8)"
                            />
                            <stop
                                offset="100%"
                                stopColor="rgba(34, 189, 255, 0.3)"
                            />
                        </linearGradient>
                    </defs>
                    <polygon
                        points={getPolygonPoints()}
                        fill="transparent"
                        stroke="#22bdff"
                        strokeWidth="2"
                    />
                </svg>
            </Box>

            {/* Chart labels */}
            {showLabels && (
                <>
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: 'calc(50% - 67px)',
                            left: 'calc(50% + 0.5px)',
                            transform: 'translateX(-50%)',
                            fontSize: '10px',
                            color: theme.palette.figma.white[80],
                            textAlign: 'center',
                            fontWeight: 400,
                            letterSpacing: '-0.3px',
                            width: '43px',
                        }}
                    >
                        {t('endurance')}
                    </Typography>
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: 'calc(50% + 55px)',
                            left: 'calc(50% + 0.5px)',
                            transform: 'translateX(-50%)',
                            fontSize: '10px',
                            color: theme.palette.figma.white[80],
                            textAlign: 'center',
                            fontWeight: 400,
                            letterSpacing: '-0.3px',
                        }}
                    >
                        {t('strength')}
                    </Typography>
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: 'calc(50% + 27px)',
                            left: 'calc(50% + 43px)',
                            fontSize: '10px',
                            color: theme.palette.figma.white[80],
                            fontWeight: 400,
                            letterSpacing: '-0.3px',
                        }}
                    >
                        {t('flexibility')}
                    </Typography>
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: 'calc(50% - 39px)',
                            left: 'calc(50% + 43px)',
                            fontSize: '10px',
                            color: theme.palette.figma.white[80],
                            fontWeight: 400,
                            letterSpacing: '-0.3px',
                        }}
                    >
                        {t('agility')}
                    </Typography>
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: 'calc(50% + 27px)',
                            left: 'calc(50% - 46px)',
                            fontSize: '10px',
                            color: theme.palette.figma.white[80],
                            textAlign: 'right',
                            fontWeight: 400,
                            letterSpacing: '-0.3px',
                            transform: 'translateX(-100%)',
                        }}
                    >
                        {t('cooperation')}
                    </Typography>
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: 'calc(50% - 39px)',
                            left: 'calc(50% - 46px)',
                            fontSize: '10px',
                            color: theme.palette.figma.white[80],
                            textAlign: 'right',
                            fontWeight: 400,
                            letterSpacing: '-0.3px',
                            transform: 'translateX(-100%)',
                        }}
                    >
                        {t('balance')}
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default RadarChart;
